import { Link } from 'react-router-dom';
import { ArrowLeft, Compass, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useCareerPath } from '../../hooks/usePracticeHub';

export function CareerPathPage() {
  const { data: careerPath, isPending, isError, error } = useCareerPath();

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="career-path-title"
    >
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link
            to="/candidate/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Compass className="h-4 w-4 text-[#ba9036]" />
            <span>Your Lighthouse career path</span>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8 lg:flex-row">
        <section className="flex-1 space-y-4" aria-labelledby="career-path-title">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle id="career-path-title" className="flex items-center gap-2 text-white">
                <Compass className="h-5 w-5 text-[#ba9036]" />
                Career path
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-200">
              {isPending && <p className="text-gray-400">Loading your career path…</p>}
              {isError && !isPending && (
                <p className="text-red-300">
                  {error instanceof Error ? error.message : 'Unable to load career path right now.'}
                </p>
              )}
              {!isPending && !isError && careerPath && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Trajectory</p>
                    <p className="text-white">
                      {careerPath.currentRole} <span className="text-gray-400">→</span> {careerPath.targetRole}
                    </p>
                    <p className="text-xs text-gray-400">{careerPath.summary}</p>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Steps</p>
                    {careerPath.steps.map((step) => (
                      <div
                        key={step.id}
                        className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 p-3"
                      >
                        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#ba9036]/15">
                          <Target className="h-4 w-4 text-[#ba9036]" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm text-white">{step.label}</p>
                            <Badge
                              className={
                                step.status === 'completed'
                                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50'
                                  : step.status === 'in-progress'
                                  ? 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                                  : 'bg-white/10 text-gray-200 border-white/20'
                              }
                            >
                              {step.status.replace('-', ' ')}
                            </Badge>
                            <span className="text-[0.7rem] text-gray-500">{step.timeline}</span>
                          </div>
                          <p className="text-xs text-gray-300">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </section>

        <aside className="w-full space-y-4 lg:w-80">
          <Card className="border-white/10 bg-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-sm text-white">Recommendations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-gray-300">
              {careerPath?.recommendations?.length ? (
                careerPath.recommendations.map((rec) => (
                  <div key={rec.id} className="rounded-lg border border-white/10 bg-black/25 p-3">
                    <p className="text-white">{rec.title}</p>
                    <p className="mt-1 text-gray-400">{rec.description}</p>
                    <Button asChild size="sm" variant="outline" className="mt-2 border-white/20 text-white">
                      <Link to={rec.actionLink}>{rec.actionLabel}</Link>
                    </Button>
                  </div>
                ))
              ) : (
                <p>No recommendations yet. Complete more profile sections and practice sessions to unlock guidance.</p>
              )}
            </CardContent>
          </Card>
        </aside>
      </main>
    </main>
  );
}
